import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  WatercraftHelmet,
  WatercraftSpinner,
  WatercraftWordmark,
} from '../../features/watercraft';
import {
  useLazyGetSubscriptionStatusQuery,
  useVerifyCheckoutMutation,
} from '../../features/watercraft/watercraftSlice';
import { verifyStripePurchase } from '../../features/watercraft/verifyCheckoutFlow';
import { WATERCRAFT_ROUTE } from '../../app/appConstants';
import { WATERCRAFT_MANAGE_ROUTE } from './constants';

type VerificationPhase = 'verifying' | 'success' | 'failed';

export default function WatercraftSuccessPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [verifyCheckout] = useVerifyCheckoutMutation();
  const [getSubscriptionStatus] = useLazyGetSubscriptionStatusQuery();
  const [phase, setPhase] = useState<VerificationPhase>('verifying');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let pollingCancelled = false;
    verifyStripePurchase({
      sessionId,
      verifyCheckout: (checkoutSessionId) =>
        verifyCheckout({ checkout_session_id: checkoutSessionId }).unwrap(),
      getSubscriptionStatus: () => getSubscriptionStatus().unwrap(),
      isCancelled: () => pollingCancelled,
    }).then((subscriptionGranted) => {
      if (pollingCancelled) {
        return;
      }
      setPhase(subscriptionGranted ? 'success' : 'failed');
    });

    return () => {
      // Cancel polling on unmount
      pollingCancelled = true;
    };
  }, [sessionId, verifyCheckout, getSubscriptionStatus]);

  return (
    <main className='grid min-h-screen w-full place-content-center gap-8 bg-gradient-to-b from-watercraft-bg to-watercraft-surface p-6 text-center text-watercraft-text-primary'>
      <WatercraftHelmet />
      <WatercraftWordmark className='mx-auto h-9 w-auto text-watercraft-text-primary' />
      <section className='mx-auto w-full max-w-sm'>
        {phase === 'verifying' && (
          <div className='flex flex-col items-center gap-4'>
            <WatercraftSpinner />
            <div>
              <h1 className='mb-2 text-xl font-semibold'>
                Confirming your subscription…
              </h1>
              <p className='text-sm text-watercraft-text-secondary'>
                Please don&apos;t close this page while we finish setting up
                your subscription.
              </p>
            </div>
          </div>
        )}
        {phase === 'success' && (
          <>
            <h1 className='mb-3 text-2xl font-bold'>You&apos;re all set</h1>
            <p className='mb-6 text-sm text-watercraft-text-secondary'>
              Your watercraft subscription is now active.
            </p>
            <Button
              type='primary'
              className='h-11 w-full rounded-lg border-none bg-watercraft-primary text-base font-semibold text-watercraft-text-primary hover:bg-watercraft-primary-light'
              onClick={() => navigate(WATERCRAFT_MANAGE_ROUTE)}
            >
              Manage my subscription
            </Button>
          </>
        )}
        {phase === 'failed' && (
          <>
            <Alert
              type='warning'
              showIcon
              message='We could not confirm your subscription yet'
              description='If you completed payment, your access may take a moment to activate. Refresh shortly, or try again.'
            />
            <Button
              className='mt-6 h-11 w-full rounded-lg border-watercraft-border text-base font-semibold text-watercraft-text-primary'
              onClick={() => navigate(WATERCRAFT_ROUTE)}
            >
              Back
            </Button>
          </>
        )}
      </section>
    </main>
  );
}
